import { useState, useEffect, useCallback } from 'react';
import { WifiOff, X, CloudOff, Cloud, Loader2, CheckCircle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getTotalPendingCount } from '@/lib/offlineStore';

interface OfflineIndicatorProps {
  /** Whether to show the indicator when online but with pending items */
  showPendingWhenOnline?: boolean;
}

export function OfflineIndicator({ showPendingWhenOnline = true }: OfflineIndicatorProps) {
  const { t } = useTranslation();
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [justSynced, setJustSynced] = useState(false);

  // Update pending count
  const updatePendingCount = useCallback(async () => {
    try {
      const count = await getTotalPendingCount();
      setPendingCount(count);
    } catch (error) {
      console.error('[OfflineIndicator] Failed to get pending count:', error);
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setIsDismissed(false);
      // Check for pending items when back online
      updatePendingCount();
    };

    const handleOffline = () => {
      setIsOffline(true);
      setIsDismissed(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for service worker messages
    const handleMessage = (event: MessageEvent) => {
      const { type } = event.data || {};
      if (type === 'SYNC_COMPLETE') {
        setIsSyncing(false);
        setJustSynced(true);
        updatePendingCount();
        // Hide success indicator after 3 seconds
        setTimeout(() => setJustSynced(false), 3000);
      } else if (type === 'SYNC_STARTED') {
        setIsSyncing(true);
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleMessage);
    }

    // Initial pending count
    updatePendingCount();

    // Poll for pending count changes
    const interval = setInterval(updatePendingCount, 5000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      }
      clearInterval(interval);
    };
  }, [updatePendingCount]);

  const handleRetry = async () => {
    setIsReconnecting(true);
    try {
      // Try to fetch a small resource to check connectivity
      await fetch('/api/v1/health', { method: 'HEAD', cache: 'no-store' });
      setIsOffline(false);
      // Trigger sync if online
      if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
        const registration = await navigator.serviceWorker.ready;
        (registration as unknown as { sync: { register: (tag: string) => Promise<void> } }).sync.register('sync-content');
      }
    } catch {
      // Still offline
    } finally {
      setIsReconnecting(false);
    }
  };

  const handleManualSync = async () => {
    if (!navigator.onLine || isSyncing) return;
    
    setIsSyncing(true);
    try {
      if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
        const registration = await navigator.serviceWorker.ready;
        (registration as unknown as { sync: { register: (tag: string) => Promise<void> } }).sync.register('sync-content');
      }
    } catch (error) {
      console.error('[OfflineIndicator] Manual sync failed:', error);
      setIsSyncing(false);
    }
  };

  // Show nothing if online, no pending items, and not just synced
  if (!isOffline && pendingCount === 0 && !justSynced) {
    return null;
  }

  // If online but has pending items, show sync status bar
  if (!isOffline && pendingCount > 0 && showPendingWhenOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-cyan-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {isSyncing ? (
                <Loader2 className="w-5 h-5 shrink-0 animate-spin" />
              ) : (
                <CloudOff className="w-5 h-5 shrink-0" />
              )}
              <div>
                <span className="font-medium">
                  {isSyncing 
                    ? t('offline.syncing', 'Szinkronizálás...')
                    : t('offline.pendingItems', '{{count}} elem vár szinkronizálásra', { count: pendingCount })
                  }
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {!isSyncing && (
                <button
                  onClick={handleManualSync}
                  className="flex items-center gap-1.5 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm font-medium"
                >
                  <Cloud className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('offline.syncNow', 'Szinkronizálás')}</span>
                </button>
              )}
              <button
                onClick={() => setIsDismissed(true)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                title={t('common.close', 'Bezárás')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show sync success message briefly
  if (!isOffline && justSynced) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-green-600 text-white shadow-lg animate-in slide-in-from-top duration-300">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="flex items-center justify-center gap-2">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">{t('offline.syncSuccess', 'Szinkronizálás sikeres!')}</span>
          </div>
        </div>
      </div>
    );
  }

  // Offline - dismissed
  if (isDismissed) {
    return null;
  }

  // Offline mode indicator
  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-amber-950 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <WifiOff className="w-5 h-5 shrink-0" />
            <div>
              <span className="font-medium">{t('offline.offlineMode', 'Offline mód')}</span>
              {pendingCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-amber-600/30 rounded-full text-xs font-medium">
                  {pendingCount} {t('offline.pending', 'függőben')}
                </span>
              )}
              <span className="hidden sm:inline text-amber-800 ml-2">
                • {t('offline.limitedFeatures', 'Néhány funkció korlátozottan érhető el')}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleRetry}
              disabled={isReconnecting}
              className="flex items-center gap-1.5 px-3 py-1 bg-amber-600/20 hover:bg-amber-600/30 rounded-lg transition-colors disabled:opacity-50 text-sm font-medium"
              title={t('offline.retry', 'Újrapróbálkozás')}
            >
              <RefreshCw className={`w-4 h-4 ${isReconnecting ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{t('offline.retry', 'Újrapróbálkozás')}</span>
            </button>
            <button
              onClick={() => setIsDismissed(true)}
              className="p-1.5 hover:bg-amber-600/20 rounded-lg transition-colors"
              title={t('common.close', 'Bezárás')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
